---
title: Rename Your Agent Sessions
description: Repo admins can rename agent sessions on Entire Web instead of relying on timestamps. Other updates fix accessibility, commit sync, CLI nightly login, and go-git parser and filesystem issues.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. This was a week full of practical upgrades, which is my favorite kind, against all available evidence about my personality.

This week, the [Entire CLI](https://github.com/entireio/cli) got mostly fixes, while on [Entire Web](https://entire.io/), we introduced editable display names for sessions, making it easier to distinguish and manage agent sessions directly from the UI. Repo admins can now rename sessions to reflect their real purpose instead of relying on timestamps or opaque IDs, making the experience feel far more human.

Meanwhile, [`go-git`](https://github.com/go-git/go-git) released `v5.19.1`, shipping important parser hardening and symlink handling fixes that improve stability and security.

All the details below in Dispatch 0x000F.

## Entire CLI

_Released via [v0.6.3-nightly](https://github.com/entireio/cli/releases/tag/v0.6.3-nightly.202605250745.b5855692)._ _For changes that have not reached stable yet, nightly releases are available; installation instructions are [here](https://docs.entire.io/cli/installation)._

- Login is now smoother: device codes are automatically copied to your clipboard during authentication.
- API responses can now be up to 16 MiB (up from 1 MiB), enabling larger session transcripts and checkpoint data to load without truncation.
- Our documentation now includes Pi in the agent listings and architecture guides.
  - Thank you, [@godswillumukoro](https://github.com/godswillumukoro), for your contribution!

## Entire Web

### UI Polish and Accessibility

- Repo admins can now edit session display names directly, enabling you to rename sessions to something meaningful like "refactor auth flow" instead of a timestamp.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.5933014354067%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/6e21b8f3f52523aeb5c33ab56de3b7b5/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F6e21b8f3f52523aeb5c33ab56de3b7b5%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Session display names demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- Inputs and dialogs are now more accessible, with better keyboard navigation and screen reader support.
- Layout separators and content chrome have been restored for cleaner visual hierarchy.
- Combobox list spacing is now more consistent.

### Repository Access and Attribution

- The Repositories page now only lists repos you've enabled Entire for, keeping unrelated repos out of your dashboard.
- Fixed a bug showing the wrong agent contributions on the overview page.

### Reliability and Performance

- Fixed session hover prefetch errors on commit pages, so hovering over sessions no longer triggers unexpected errors.
- Commit sync no longer misses commits during filtered syncs.
- Fixed commit sync deadlocks that could stall repository updates.
- The hydrator (which processes session data) is now faster and more reliable.

## go-git

_Released via [v5.19.1](https://github.com/go-git/go-git/releases/tag/v5.19.1) and [v6.0.0-alpha.4](https://github.com/go-git/go-git/releases/tag/v6.0.0-alpha.4)._

### Security Fixes

- These releases include mitigations for three recently disclosed vulnerabilities affecting supported `go-git` release lines:
  - [GHSA-w5pp-99ch-qj29](https://github.com/go-git/go-git/security/advisories/GHSA-w5pp-99ch-qj29): Hardened handling for malformed Git object data that could trigger panics or excessive resource usage.
  - [GHSA-crhj-59gh-8x96](https://github.com/go-git/go-git/security/advisories/GHSA-crhj-59gh-8x96): Fixed an issue where crafted repositories could modify main and submodule `.git` directories.
  - [GHSA-389r-gv7p-r3rp](https://github.com/go-git/go-git/security/advisories/GHSA-389r-gv7p-r3rp): Improved parsing behavior for specially crafted objects so go-git stays aligned with upstream Git interpretation.

### Parser Hardening

- Delta chain depth is now capped in the parser, preventing malformed packfiles from causing excessive resource usage.
- Format decoder input bounds are now hardened, improving robustness against malformed Git data.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

### Filesystem Handling

- Symlink target paths are no longer validated unnecessarily, fixing edge cases where valid repositories could fail to checkout.
- `FSObject.Reader()` now uses `ReadAt`, improving concurrency safety.
  - Thank you, [@cedric-appdirect](https://github.com/cedric-appdirect), for your contribution!
- `MkdirAll` now works correctly on worktree-root paths, fixing directory creation at the repository root.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

### go-billy Filesystem Updates

- Added experimental `WithMmap` support for best-effort read-only `Open` operations.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

That's the dispatch. As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or observations about the nature of naming things. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug.

Now, let us continue our slow march to the end of the Universe.

Boop.
